import {
  CarbonIntensity,
  CarbonIntensities
} from '@/models/carbon/CarbonIntensity.js'
import Modules from '@/models/Modules.js'
import { Tag } from '@/models/core/TagCategory'
import {
  CommuteCollection,
  TravelCollection,
  ModuleCollection
} from '@/models/carbon/Collection.js'
import { CarbonSynthesis, LCASynthesis } from '@/models/carbon/Synthesis'
import { entityFactory } from '../core/entity/factory'
import { Laboratory } from '../core/entity/Laboratory'
import { Boundary } from './Boundary'

import { ActiveModules } from '../ActiveModules'

/**
 * Get the module class from which the static compute method will be called
 *
 * @param {string} type
 * @returns
 */
function _getModuleClass (activeModulesObjs, type) {
  let klass = activeModulesObjs.byType(type)?.klass
  if (!klass) {
    // fallback to default module class
    return ActiveModules.getClasse(type)
  }
  return klass
}

/**
 * Central piece of the app.
 * Represents a GHGI, the associated entity, boundary and data.
 *
 */
class GHGI {
  constructor ({
    id = null,
    uuid = null,
    year = null,
    boundary = null,
    created = null,
    description = null,
    surveyMessage = null,
    surveyCloneYear = null,
    commutesActive = true,
    foodsActive = true,
    surveyTags = [],
    entity = null,
    isSubmitted = false,
    buildings = [],
    purchases = [],
    devices = [],
    vehicles = [],
    travels = [],
    commutes = [],
    foods = [],
    ractivities = [],
    carbonSynthesis = null,
    lcaSynthesis = null,
    emissions = {},
    activeModulesObjs = null
  } = {}) {
    this.id = id
    this.uuid = uuid
    this.year = year
    this.created = new Date(created)
    this.description = description

    // FIXME(msimonin): sanity check those two ?
    // list of Member
    this.boundary = boundary

    this.surveyMessage = surveyMessage
    this.surveyCloneYear = surveyCloneYear
    this.commutesActive = commutesActive
    this.foodsActive = foodsActive
    this.surveyTags = surveyTags.map((obj) => Tag.createFromObj(obj))

    if (entity) {
      this.entity = entity
    } else {
      this.entity = {
        area: null,
        country: null,
        citySize: null
      }
    }

    this.isSubmitted = isSubmitted

    // keep track of the activeModules
    // fallback to an empty one
    this.activeModulesObjs = activeModulesObjs || new ActiveModules()

    // Create collections
    let computeFnc = _getModuleClass(
      this.activeModulesObjs,
      Modules.BUILDINGS
    ).compute
    this.buildings = new ModuleCollection(computeFnc)

    computeFnc = _getModuleClass(activeModulesObjs, Modules.PURCHASES).compute
    this.purchases = new ModuleCollection(computeFnc)

    computeFnc = _getModuleClass(activeModulesObjs, Modules.DEVICES).compute
    this.devices = new ModuleCollection(computeFnc)

    computeFnc = _getModuleClass(activeModulesObjs, Modules.VEHICLES).compute
    this.vehicles = new ModuleCollection(computeFnc)

    computeFnc = _getModuleClass(activeModulesObjs, Modules.TRAVELS).compute
    this.travels = new TravelCollection(computeFnc)

    computeFnc = _getModuleClass(activeModulesObjs, Modules.COMMUTES).compute
    this.commutes = new CommuteCollection(computeFnc)

    computeFnc = _getModuleClass(activeModulesObjs, Modules.FOODS).compute
    this.foods = new ModuleCollection(computeFnc)

    computeFnc = _getModuleClass(activeModulesObjs, Modules.RACTIVITIES).compute
    this.ractivities = new ModuleCollection(computeFnc)

    // Add objects to collections
    buildings.map((obj) => this.buildings.add(obj))
    purchases.map((obj) => this.purchases.add(obj))
    devices.map((obj) => this.devices.add(obj))
    vehicles.map((obj) => this.vehicles.add(obj))
    travels.map((obj) => this.travels.add(obj))
    commutes.map((obj) => this.commutes.add(obj))
    foods.map((obj) => this.foods.add(obj))
    ractivities.map((obj) => this.ractivities.add(obj))

    // computed properties
    this.carbonSynthesis = CarbonSynthesis.createFromObj(carbonSynthesis)
    this.lcaSynthesis = LCASynthesis.createFromObj(lcaSynthesis)
    this.emissions = emissions
  }

  add (module, data) {
    // we have date but the corresponding module but it's not activated
    if (!this.activeModulesObjs.byType(module)) {
      return
    }
    // set the consumption data
    let dataObjs = data.map((d) =>
      this.activeModulesObjs.byType(module).loads(d)
    )

    dataObjs.forEach((b) => this[module].add(b))
  }

  get surveyActive () {
    return this.commutesActive || this.foodsActive
  }

  get workforce () {
    return this.boundary.workforce
  }

  get intensity () {
    let intensity = new CarbonIntensities()
    for (let module of this.activeModulesObjs) {
      if (Modules.MODULES[module.type].length === 0) {
        intensity.add(this.emissions[module.type].intensity)
      } else {
        for (let submodule of Modules.MODULES[module.type]) {
          intensity.add(this.emissions[module.type][submodule].intensity)
        }
      }
    }
    return intensity.sum()
  }

  updateSubmitted () {
    this.isSubmitted = !this.isSubmitted
  }

  activateDesactivateSurvey (module = null) {
    if (module === Modules.FOODS) {
      this.foodsActive = !this.foodsActive
    } else if (module === Modules.COMMUTES) {
      this.commutesActive = !this.commutesActive
    } else {
      this.foodsActive = !this.foodsActive
      this.commutesActive = !this.commutesActive
    }
  }

  toString (sep = '\t') {
    let value = [this.uuid, this.created, this.year].join(sep)
    value += sep + this.entity.toString(sep)
    value += sep + this.boundary.toString(sep)
    value += sep + this.isSubmitted
    value += sep + this.carbonSynthesis.toString(sep)
    value += sep + this.lcaSynthesis.toString(sep)
    return value
  }

  /**
   * Whether the module(type or subtype) needs to be computed
   * specification:
   * - if the module is not activated -> false
   * - null, y -> true
   * - x, y -> true iff the parent module of x is y (e.g electricity, buidings)
   *
   * @param {string} module
   * @param {string} type
   * @returns {boolean} - true iff the module needs to be computed
   */
  _shouldCompute (module, type) {
    let klass = this.activeModulesObjs.byType(type)?.klass
    let ok =
      klass && (!module || (module && Modules.getParentModule(module) === type))
    return ok
  }

  _computeBuildings (year, tags) {
    return this.buildings.compute(
      this.entity.area,
      this.entity.country,
      year,
      tags
    )
  }

  _computeCommutes (year, tags, commuteSettings) {
    return this.commutes.compute(
      this.entity.citySize,
      this.boundary,
      year,
      commuteSettings,
      tags
    )
  }

  _computeFoods (year, tags, commuteSettings) {
    return this.foods.compute(
      this.boundary.workforce,
      year,
      commuteSettings,
      tags
    )
  }

  _computeVehicles (year, tags) {
    return this.vehicles.compute(year, tags)
  }

  _computeTravels (
    year,
    tags,
    { TRAVEL_POSITION_LABELS = [], TRAVEL_PURPOSE_LABELS = [] } = {}
  ) {
    return this.travels.compute(
      year,
      tags,
      TRAVEL_POSITION_LABELS,
      TRAVEL_PURPOSE_LABELS
    )
  }

  _computeDevices (tags) {
    return this.devices.compute(tags)
  }

  _computePurchases (year, tags) {
    return this.purchases.compute(year, tags)
  }

  _computeRActivities (year, tags) {
    return this.ractivities.compute(year, tags)
  }

  compute (options = null, year = null, tags = [], module = null) {
    let cyear = this.year
    if (year !== null) {
      cyear = year
    }

    if (this._shouldCompute(module, Modules.BUILDINGS)) {
      this.emissions.buildings = this._computeBuildings(cyear, tags)
    }

    if (this._shouldCompute(module, Modules.COMMUTES)) {
      this.emissions.commutes = this._computeCommutes(cyear, tags, options)
    }

    if (this._shouldCompute(module, Modules.FOODS)) {
      this.emissions.foods = this._computeFoods(cyear, tags, options)
    }

    if (this._shouldCompute(module, Modules.VEHICLES)) {
      this.emissions.vehicles = this._computeVehicles(cyear, tags)
    }

    if (this._shouldCompute(module, Modules.TRAVELS)) {
      this.emissions.travels = this._computeTravels(cyear, tags, options)
    }

    if (this._shouldCompute(module, Modules.DEVICES)) {
      this.emissions.devices = this._computeDevices(tags)
    }

    if (this._shouldCompute(module, Modules.PURCHASES)) {
      this.emissions.purchases = this._computePurchases(cyear, tags)
    }

    if (this._shouldCompute(module, Modules.RACTIVITIES)) {
      this.emissions.ractivities = this._computeRActivities(cyear, tags)
    }

    // Update synthesis
    this.carbonSynthesis = new CarbonSynthesis({
      heatings: this.emissions?.buildings?.heatings?.intensity,
      electricity:
        this.emissions?.buildings?.electricity?.intensity?.toCarbonIntensity(),
      refrigerants: this?.emissions?.buildings?.refrigerants?.intensity,
      water: this?.emissions?.buildings?.water?.intensity,
      construction: this?.emissions?.buildings?.construction?.intensity,
      commutes: this?.emissions?.commutes?.intensity,
      foods: this?.emissions?.foods?.intensity,
      travels: this?.emissions?.travels?.intensity,
      vehicles: this?.emissions?.vehicles?.intensity,
      devices: this?.emissions?.devices?.intensity,
      purchases: this?.emissions?.purchases?.intensity,
      ractivities: this?.emissions?.ractivities?.intensity
    })
    this.lcaSynthesis = new LCASynthesis({
      heatings: this.emissions?.buildings?.heatings?.impact.intensity,
      electricity: this.emissions?.buildings?.electricity?.impact.intensity,
      refrigerants: this?.emissions?.buildings?.refrigerants?.impact.intensity,
      water: this?.emissions?.buildings?.water?.impact.intensity,
      construction: this?.emissions?.buildings?.construction?.impact.intensity,
      commutes: this?.emissions?.commutes?.impact.intensity,
      foods: this?.emissions?.foods?.impact.intensity,
      travels: this?.emissions?.travels?.impact.intensity,
      vehicles: this?.emissions?.vehicles?.impact.intensity,
      devices: this?.emissions?.devices?.impact.intensity,
      purchases: this?.emissions?.purchases?.impact.intensity
    })
  }

  static exportHeader (positionTitles, sep = '\t') {
    let value = ['ghgi.uuid', 'created.date', 'year'].join(sep)
    value += sep + Laboratory.exportHeader(sep)
    value += sep + Boundary.exportHeader(positionTitles, sep)
    value += sep + 'isSubmitted'
    value += sep + CarbonSynthesis.exportHeader(sep)
    value += sep + LCASynthesis.exportHeader(sep)
    return value
  }

  static exportToFile (
    positionTitles,
    items,
    header = true,
    extraColValue = null,
    sep = '\t'
  ) {
    let headerValues = []
    if (header) {
      headerValues = GHGI.exportHeader(positionTitles, sep)
    }
    return [
      headerValues,
      ...items.map(function (item) {
        let val = ''
        if (extraColValue) {
          val += extraColValue + sep
        }
        return val + item.toString(sep)
      })
    ]
      .join('\n')
      .replace(/(^\[)|(\]$)/gm, '')
  }

  /**
   * Create a GHGI from an obj (json-like)
   * Deal only with first level attributes (except for entity though)
   *
   * To build a full-fledge GHGI make use of make @see {@link makeGHGI}
   *
   * @param {*} item
   * @returns
   */

  static createFromObj (item, activeModulesObjs) {
    let e = item.entity
    let entity = entityFactory(e.type).createFromObj(e.data)
    // activity data must be set outside of this function using the add* methods
    // boundary must also be set outside this function
    return new GHGI({
      id: item.id,
      uuid: item.uuid,
      year: item.year,
      created: item.created,
      description: item.description,
      surveyMessage: item.surveyMessage,
      surveyCloneYear: item.surveyCloneYear,
      commutesActive: item.commutesActive,
      foodsActive: item.foodsActive,
      surveyTags: item.surveyTags,
      entity: entity,
      isSubmitted: item.isSubmitted,
      carbonSynthesis: item.carbonSynthesis,
      lcaSynthesis: item.lcaSynthesis,
      emissions: item.emissions,
      activeModulesObjs: activeModulesObjs
    })
  }

  /**
   * Build a report for the various modules.
   * Also aggregates some intensities for the modules issued as parameter.
   *
   * /!\ the report is generated from the last computed emissions.
   * (one must call this.compute before calling this method)
   *
   * Aggregates definitions:
   * - proTravels: sum of the intensities of all professional travels (vehicules,
   *  travels/missions)
   * - allTravels: proTravels + commutes
   * - total: sum of the intensities
   *
   * @param {string[]} modules - list of modules to account for the various
   *  aggregates
   * @returns {Object} report (custom schema)
   */
  buildL1P5Report (modules) {
    let emissions = this.emissions

    let s = {
      buildings: {
        usage: new CarbonIntensity(),
        heatings: new CarbonIntensity(),
        electricity: new CarbonIntensity(),
        refrigerants: new CarbonIntensity(),
        water: new CarbonIntensity(),
        construction: new CarbonIntensity(),
        total: new CarbonIntensity()
      },
      vehicles: {
        total: new CarbonIntensity()
      },
      ractivities: {
        total: new CarbonIntensity(),
        types: {}
      },
      devices: {
        total: new CarbonIntensity(),
        types: {}
      },
      commutes: {
        total: new CarbonIntensity()
      },
      travels: {
        total: new CarbonIntensity()
      },
      purchases: {
        total: new CarbonIntensity(),
        amortized: new CarbonIntensity(),
        nonamortized: new CarbonIntensity(),
        categories: {}
      },
      foods: {
        total: new CarbonIntensity(),
        meals: {
          intensity: {}
        }
      },
      // aggregates
      proTravels: new CarbonIntensities(),
      allTravels: new CarbonIntensities(),
      total: new CarbonIntensities()
    }

    if (!emissions) {
      return s
    }

    let results = emissions.buildings
    if (results && modules.includes(Modules.BUILDINGS)) {
      s.buildings.heatings = results.heatings.intensity
      s.buildings.electricity = results.electricity.intensity
      s.buildings.refrigerants = results.refrigerants.intensity
      s.buildings.water = results.water.intensity
      s.buildings.construction = results.construction.intensity

      // usage only
      let usage = new CarbonIntensities()
      usage.add(results.heatings.intensity)
      usage.add(results.electricity.intensity)
      usage.add(results.water.intensity)
      usage.add(results.refrigerants.intensity)
      s.buildings.usage = usage.sum()

      // total of buildings
      let total = new CarbonIntensities()
      total.add(results.heatings.intensity)
      total.add(results.electricity.intensity)
      total.add(results.water.intensity)
      total.add(results.refrigerants.intensity)
      total.add(results.construction.intensity)
      s.buildings.total = total.sum()
      s.total.add(results.construction.intensity)
      s.total.add(usage.sum())
    }

    results = emissions.vehicles
    if (results && modules.includes(Modules.VEHICLES)) {
      s.vehicles.total = results.intensity
      s.proTravels.add(results.intensity)
      s.allTravels.add(results.intensity)
      s.total.add(results.intensity)
    }

    results = emissions.ractivities
    if (results && modules.includes(Modules.RACTIVITIES)) {
      s.ractivities.total = results.intensity
      s.ractivities.types = results.types
      s.total.add(results.intensity)
    }

    results = emissions.devices
    if (results && modules.includes(Modules.DEVICES)) {
      s.devices.total = results.intensity
      s.devices.types = results.types
      s.total.add(results.intensity)
    }

    results = emissions.commutes
    if (results && modules.includes(Modules.COMMUTES)) {
      s.commutes.total = results.intensity
      s.allTravels.add(results.intensity)
      s.total.add(results.intensity)
    }

    results = emissions.foods
    if (results && modules.includes(Modules.FOODS)) {
      s.foods.total = results.intensity
      s.foods.meals = results.meals
      s.total.add(results.intensity)
    }

    results = emissions.travels
    if (results && modules.includes(Modules.TRAVELS)) {
      let intensity = new CarbonIntensity(
        results.intensity.intensity,
        results.intensity.uncertainty
      )
      s.travels.total = intensity

      s.proTravels.add(intensity)
      s.allTravels.add(intensity)
      s.total.add(intensity)
    }

    results = emissions.purchases
    if (results && modules.includes(Modules.PURCHASES)) {
      s.purchases.total = results.intensity
      s.purchases.amortized = results.amort
      s.purchases.nonamortized = results.nonamort
      s.purchases.categories = results.categories
      s.total.add(results.intensity)
    }
    return s
  }
}

export { GHGI as default }
